import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Divider,
  FormControlLabel,
  Grid,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import RoomOutlinedIcon from "@mui/icons-material/RoomOutlined";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import { getGooglePlacesSettings, saveGooglePlacesSettings } from "../../services/googlePlaces";

const defaults = {
  googlePlacesEnabled: false,
  googlePlacesApiKey: "",
  hasApiKey: false,
  apiKeyMasked: "",
};

function getErrorMessage(error, fallback) {
  return error?.response?.data?.message || error?.response?.data?.error || fallback;
}

export default function GooglePlacesSettings({ instanceUniqueId }) {
  const [form, setForm] = useState(defaults);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState(null);

  const applyPayload = (payload = {}) => {
    const googlePlaces = payload?.googlePlaces || {};
    setForm({
      ...defaults,
      googlePlacesEnabled: googlePlaces.enabled === true,
      googlePlacesApiKey: googlePlaces.apiKey || "",
      hasApiKey: googlePlaces.hasApiKey === true,
      apiKeyMasked: googlePlaces.apiKeyMasked || "",
    });
  };

  const load = async () => {
    if (!instanceUniqueId) return;
    setLoading(true);
    setNotice(null);
    try {
      const payload = await getGooglePlacesSettings(instanceUniqueId);
      applyPayload(payload);
    } catch (error) {
      setNotice({
        severity: "error",
        message: getErrorMessage(error, "Failed to load Google Maps / Places settings."),
      });
    } finally {
      setLoading(false);
    }
  };

  const save = async () => {
    if (form.googlePlacesEnabled && !form.hasApiKey && !String(form.googlePlacesApiKey || "").trim()) {
      setNotice({
        severity: "warning",
        message: "Add a Google Maps / Places API key before enabling smart address search.",
      });
      return;
    }

    setSaving(true);
    setNotice(null);
    try {
      const payload = await saveGooglePlacesSettings(instanceUniqueId, {
        googlePlacesEnabled: form.googlePlacesEnabled,
        googlePlacesApiKey: form.googlePlacesApiKey,
      });
      applyPayload(payload);
      setNotice({
        severity: "success",
        message: "Google Maps / Places settings saved.",
      });
    } catch (error) {
      setNotice({
        severity: "error",
        message: getErrorMessage(error, "Failed to save Google Maps / Places settings."),
      });
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    load();
  }, [instanceUniqueId]);

  return (
    <Card elevation={3} sx={{ borderRadius: 2 }}>
      <CardHeader
        avatar={<RoomOutlinedIcon color="primary" />}
        title={(
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography variant="h6">Google Maps / Places</Typography>
            {loading ? <CircularProgress size={16} /> : null}
          </Box>
        )}
        action={(
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={load}
              disabled={loading || saving}
            >
              Refresh
            </Button>
            <Button
              variant="contained"
              startIcon={<SaveOutlinedIcon />}
              onClick={save}
              disabled={loading || saving}
            >
              {saving ? "Saving..." : "Save"}
            </Button>
          </Box>
        )}
        subheader="Configure browser-safe Google Places autocomplete per instance."
      />
      <Divider />
      <CardContent>
        {notice ? <Alert severity={notice.severity} sx={{ mb: 2 }}>{notice.message}</Alert> : null}

        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Alert severity="info" sx={{ mb: 1 }}>
              Use a browser-restricted Google key with only the required Maps JavaScript API and Places API enabled. Add
              `localhost` for development and your production Elite Core domains in HTTP referrer restrictions.
            </Alert>
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              control={(
                <Switch
                  checked={form.googlePlacesEnabled}
                  onChange={(event) => {
                    const checked = event.target.checked;
                    setForm((current) => ({ ...current, googlePlacesEnabled: checked }));
                    if (checked && !form.hasApiKey && !String(form.googlePlacesApiKey || "").trim()) {
                      setNotice({
                        severity: "warning",
                        message: "A saved or newly entered API key is required before smart address search can be enabled.",
                      });
                    } else {
                      setNotice(null);
                    }
                  }}
                />
              )}
              label="Enable Smart Address Search"
            />
          </Grid>
          <Grid item xs={12} md={8}>
            <TextField
              fullWidth
              type="password"
              label="Google Maps / Places API Key"
              value={form.googlePlacesApiKey}
              onChange={(event) => setForm((current) => ({ ...current, googlePlacesApiKey: event.target.value }))}
              placeholder={form.hasApiKey ? form.apiKeyMasked || "Configured" : "Enter browser-restricted API key"}
              helperText={form.hasApiKey
                ? `Saved: ${form.apiKeyMasked}. Leave blank to keep the existing key.`
                : "The key must have Google Maps / Places APIs enabled for browser autocomplete."}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <Alert severity={form.googlePlacesEnabled ? "success" : "warning"} sx={{ height: "100%", alignItems: "flex-start" }}>
              {form.googlePlacesEnabled
                ? "Smart address search is enabled for this instance when the saved key is valid."
                : "When disabled, address fields should fall back to manual entry."}
            </Alert>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
}
